import React, { useMemo } from 'react';
import { AudioPlayerState } from '@/hooks/use-audio-player';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { Text, View } from 'react-native';
import { TextSegment, VoicemailWordTimestamp } from './types';
import { useInboundMessageStyles } from './utils';

const formatSecondsAsTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

interface Props {
  audioPlayerState: AudioPlayerState;
  keyPrefix: string;
  onSelect?: (matchTerm: string, timestampMS: number | null) => void;
  segments: TextSegment[];
  selectedInputUUID?: string;
}

export const OriginalInboundTimestamps: React.FC<Props> = ({
  audioPlayerState,
  keyPrefix,
  onSelect,
  segments,
  selectedInputUUID,
}) => {
  const [inboundMessageStyles] = useInboundMessageStyles();
  const [styles] = useStyles();

  const content = useMemo<React.ReactNode[]>(() => {
    const contentSegments: React.ReactNode[] = [];
    let trailingTimestamp: VoicemailWordTimestamp | null = null;
    segments.forEach((segment, index) => {
      const timestampSegments: React.ReactNode[] = [];

      // Guard against edge cases where a segment doesn't have a timestamp
      // by using the trailing timestamp of the previous segment
      const nextTimestamp = segment.timestamp
        ? Math.floor(segment.timestamp.startSeconds)
        : trailingTimestamp
          ? Math.ceil(
              trailingTimestamp.endSeconds ?? audioPlayerState.max.value,
            )
          : null;

      if (typeof nextTimestamp === 'number') {
        timestampSegments.push(
          <Text
            key={`${keyPrefix}-${index}-timestamp`}
            style={styles.timestampText}
          >
            {formatSecondsAsTime(nextTimestamp)}
          </Text>,
        );
      }

      if (segment.timestamp) {
        trailingTimestamp = segment.timestamp;
      }

      if (segment.type === 'match') {
        timestampSegments.push(
          <View
            key={`${keyPrefix}-${index}-match`}
            style={styles.matchContainer}
          >
            <Text
              onPress={
                onSelect && segment.searchInputUUID
                  ? () => {
                      onSelect(
                        segment.searchInputUUID!,
                        nextTimestamp ? nextTimestamp * 1000 : null,
                      );
                    }
                  : undefined
              }
              style={[
                TYPOGRAPHY.description,
                inboundMessageStyles.text,
                inboundMessageStyles.textHighlighted,
                selectedInputUUID === segment.searchInputUUID
                  ? inboundMessageStyles.textSelected
                  : null,
                styles.matchText,
              ]}
            >
              {segment.text}
            </Text>
          </View>,
        );
      } else {
        timestampSegments.push(
          <Text
            key={`${keyPrefix}-${index}-text`}
            style={[TYPOGRAPHY.description, inboundMessageStyles.text]}
          >
            {segment.text}
          </Text>,
        );
      }
      contentSegments.push(
        <View key={`${keyPrefix}-${index}`} style={styles.timestampContainer}>
          {timestampSegments}
        </View>,
      );
    });
    return contentSegments;
  }, [
    audioPlayerState.max.value,
    inboundMessageStyles,
    keyPrefix,
    onSelect,
    segments,
    selectedInputUUID,
    styles,
  ]);

  return <View collapsable={false}>{content}</View>;
};

const useStyles = createStyles((theme) => ({
  matchContainer: {
    flexDirection: 'row',
  },
  matchText: {
    flexShrink: 1,
  },
  timestampContainer: {
    marginBottom: 12,
  },
  timestampText: {
    ...FONTS.primaryBold,
    color: theme.sk.color.text.tertiary,
    fontSize: 10,
    lineHeight: 12,
  },
}));
